import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import DataTable from "../components/common/Table";
import Modal from "../components/common/Modal";
import ConfirmModal from "../components/common/ConfirmModal";
import api from "../services/api";
import useStore from "../store/useStore";
import { resolveAssetUrl } from "../utils/assetUrl";
import {
  COUNTRY_CODE_OPTIONS,
  splitPhoneWithCountryCode,
  buildPhoneWithCountryCode
} from "../utils/phoneCountryCodes";

const initialForm = {
  companyName: "",
  email: "",
  countryCode: "+220",
  phone: "",
  address: "",
  contactPerson: "",
  logo: "",
  subscriptionStatus: "Inactive",
  subscriptionStart: "",
  subscriptionEnd: "",
  active: 0
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const computeEndDate = (startDate, cycle) => {
  if (!startDate) return "";
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return "";
  const end = new Date(start);
  if (cycle === "Yearly") {
    end.setFullYear(end.getFullYear() + 1);
  } else {
    end.setMonth(end.getMonth() + 1);
  }
  end.setDate(end.getDate() - 1);
  return end.toISOString().split("T")[0];
};

const isSubscriptionDue = (company) => {
  const status = String(company.subscriptionStatus || "").toLowerCase();
  if (Number(company.active) === 0 || status !== "active") return true;
  if (!company.subscriptionEnd) return true;
  const end = new Date(company.subscriptionEnd);
  if (Number.isNaN(end.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return end < today;
};

const toDateInput = (value) => (value ? String(value).split("T")[0] : "");

const SuperAdminCompanies = () => {
  const { toast } = useStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    subscriptionPlan: "",
    subscriptionCycle: "Monthly",
    subscriptionAmount: 0,
    subscriptionCurrency: "GMD",
    subscriptionStart: "",
    subscriptionEnd: "",
    dueDate: "",
    paymentContactMethod: "WhatsApp",
    subscriptionNotes: ""
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState(initialForm);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/companies");
      setCompanies(res.data?.data || []);
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast(error.response?.data?.message || "Failed to load companies", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await api.get("/subscription-plans");
        setSubscriptionPlans(res.data?.data || []);
      } catch (error) {
        console.error("Load subscription plans error:", error);
      }
    };

    loadPlans();
  }, []);

  const getPlanAmount = (planName, cycle) => {
    const plan = subscriptionPlans.find((item) => item.name === planName);
    if (!plan) return 0;
    return cycle === "Yearly" ? Number(plan.yearlyPrice || 0) : Number(plan.monthlyPrice || 0);
  };

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;

    return companies.filter((company) => {
      const haystack = `${company.companyName || ""} ${company.email || ""} ${company.phone || ""} ${company.address || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [companies, search]);

  const openCreate = () => {
    setEditingCompany(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEdit = (company) => {
    const parsedPhone = splitPhoneWithCountryCode(company.phone || "", "+220");

    setEditingCompany(company);
    setForm({
      companyName: company.companyName || "",
      email: company.email || "",
      countryCode: parsedPhone.countryCode,
      phone: parsedPhone.number,
      address: company.address || "",
      contactPerson: company.contactPerson || "",
      logo: company.logo || "",
      subscriptionStatus: company.subscriptionStatus || "Inactive",
      subscriptionStart: company.subscriptionStart ? company.subscriptionStart.split('T')[0] : "",
      subscriptionEnd: company.subscriptionEnd ? company.subscriptionEnd.split('T')[0] : "",
      active: company.active ?? 0
    });
    setModalOpen(true);
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setModalOpen(false);
    setEditingCompany(null);
    setForm(initialForm);
  };

  const handleLogoFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      toast("Please choose an image file for the company logo", "error");
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      toast("Logo image must be 2MB or smaller", "error");
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(selectedFile);
      setForm((prev) => ({ ...prev, logo: dataUrl }));
    } catch (error) {
      toast("Failed to read logo image", "error");
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.companyName.trim() || !form.email.trim()) {
      toast("Company name and email are required", "error");
      return;
    }

    try {
      setSaving(true);

      if (editingCompany) {
        await api.put(`/companies/${editingCompany.id}`, {
          companyName: form.companyName.trim(),
          email: form.email.trim(),
          phone: buildPhoneWithCountryCode(form.countryCode, form.phone),
          address: form.address.trim(),
          contactPerson: form.contactPerson.trim(),
          logo: form.logo || null,
          subscriptionStatus: form.subscriptionStatus,
          subscriptionStart: form.subscriptionStart || null,
          subscriptionEnd: form.subscriptionEnd || null,
          active: Number(form.active)
        });
        toast("Company updated successfully", "success");
      } else {
        const response = await api.post("/companies", {
          companyName: form.companyName.trim(),
          email: form.email.trim(),
          phone: buildPhoneWithCountryCode(form.countryCode, form.phone),
          address: form.address.trim(),
          contactPerson: form.contactPerson.trim(),
          logo: form.logo || null
        });
        toast(response.data?.message || "Company created successfully", "success");
      }

      closeModal(true);
      await fetchCompanies();
    } catch (error) {
      console.error("Save company error:", error);
      toast(error.response?.data?.message || "Failed to save company", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeletingId(deleteTarget.id);
      await api.delete(`/companies/${deleteTarget.id}`);
      toast("Company deleted successfully", "success");
      setDeleteTarget(null);
      await fetchCompanies();
    } catch (error) {
      console.error("Delete company error:", error);
      toast(error.response?.data?.message || "Failed to delete company", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const openInvoice = (company) => {
    const today = new Date().toISOString().split("T")[0];
    const plan = company.subscriptionPlan || subscriptionPlans[0]?.name || "";
    const cycle = company.subscriptionCycle || "Monthly";
    const start = toDateInput(company.subscriptionStart) || today;
    const end = toDateInput(company.subscriptionEnd) || computeEndDate(start, cycle);

    setInvoiceTarget(company);
    setInvoiceForm({
      subscriptionPlan: plan,
      subscriptionCycle: cycle,
      subscriptionAmount: Number(company.subscriptionAmount || getPlanAmount(plan, cycle)),
      subscriptionCurrency: company.subscriptionCurrency || "GMD",
      subscriptionStart: start,
      subscriptionEnd: end,
      dueDate: end || today,
      paymentContactMethod: company.paymentContactMethod || "WhatsApp",
      subscriptionNotes: company.subscriptionNotes || ""
    });
  };

  const closeInvoice = (force = false) => {
    if (sendingInvoice && !force) return;
    setInvoiceTarget(null);
  };

  const onInvoiceFieldChange = (e) => {
    const { name, value } = e.target;
    setInvoiceForm((prev) => {
      const next = { ...prev, [name]: name === "subscriptionAmount" ? Number(value) : value };
      if (name === "subscriptionPlan" || name === "subscriptionCycle") {
        next.subscriptionAmount = getPlanAmount(next.subscriptionPlan, next.subscriptionCycle);
        if (next.subscriptionStart) {
          next.subscriptionEnd = computeEndDate(next.subscriptionStart, next.subscriptionCycle);
          next.dueDate = next.subscriptionEnd;
        }
      }
      if (name === "subscriptionStart") {
        next.subscriptionEnd = computeEndDate(value, next.subscriptionCycle);
        next.dueDate = next.subscriptionEnd;
      }
      return next;
    });
  };

  const handleSendInvoice = async () => {
    if (!invoiceTarget?.id) return;
    if (!invoiceTarget.email) {
      toast("Cannot send invoice without a company email", "error");
      return;
    }
    if (!invoiceForm.subscriptionPlan) {
      toast("Select a subscription plan first", "error");
      return;
    }
    if (Number(invoiceForm.subscriptionAmount) <= 0) {
      toast("Invoice amount must be greater than 0", "error");
      return;
    }

    try {
      setSendingInvoice(true);
      const res = await api.post(`/companies/${invoiceTarget.id}/invoice`, {
        email: invoiceTarget.email,
        subscriptionPlan: invoiceForm.subscriptionPlan,
        subscriptionCycle: invoiceForm.subscriptionCycle,
        subscriptionAmount: Number(invoiceForm.subscriptionAmount),
        subscriptionCurrency: invoiceForm.subscriptionCurrency || "GMD",
        subscriptionStart: invoiceForm.subscriptionStart,
        subscriptionEnd: invoiceForm.subscriptionEnd,
        dueDate: invoiceForm.dueDate || invoiceForm.subscriptionEnd,
        paymentContactMethod: invoiceForm.paymentContactMethod,
        subscriptionNotes: invoiceForm.subscriptionNotes.trim() || null
      });
      toast(res.data?.message || `Invoice sent to ${invoiceTarget.email}`, "success");
      closeInvoice(true);
      await fetchCompanies();
    } catch (error) {
      toast(error.response?.data?.message || "Failed to send invoice", "error");
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleApprove = async (company) => {
    if (!company.subscriptionPlan || Number(company.subscriptionAmount) <= 0) {
      toast("Send an invoice with a plan and amount before approving this account.", "error");
      openInvoice(company);
      return;
    }
    try {
      const res = await api.put(`/companies/${company.id}`, {
        companyName: company.companyName,
        email: company.email,
        phone: company.phone,
        address: company.address,
        contactPerson: company.contactPerson || "",
        subscriptionStatus: "Active",
        subscriptionPlan: company.subscriptionPlan,
        subscriptionCycle: company.subscriptionCycle,
        subscriptionAmount: Number(company.subscriptionAmount || 0),
        subscriptionCurrency: company.subscriptionCurrency || "GMD",
        subscriptionStart: company.subscriptionStart || new Date().toISOString().split("T")[0],
        subscriptionEnd: company.subscriptionEnd || computeEndDate(new Date().toISOString().split("T")[0], company.subscriptionCycle || "Monthly"),
        paymentContactMethod: company.paymentContactMethod || "WhatsApp",
        subscriptionNotes: company.subscriptionNotes || null,
        active: 1
      });
      toast(res.data?.message || "Company approved successfully", "success");
      fetchCompanies();
    } catch (error) {
      toast(error.response?.data?.message || "Failed to approve company", "error");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Companies Management"
        action={
          <Button variant="primary" icon="add" onClick={openCreate}>
            New Company
          </Button>
        }
      />

      <p className="text-sm text-gray-600">
        Total companies: <span className="font-bold text-gray-900">{companies.length}</span>
      </p>

      <DataTable
        columns={[
          {
            key: "companyName",
            label: "Company",
            render: (company) => (
              <div className="flex items-center gap-3 font-semibold">
                {resolveAssetUrl(company.logo) ? (
                  <img src={resolveAssetUrl(company.logo)} alt="" className="h-8 w-8 rounded-md border border-gray-200 object-contain bg-white" />
                ) : null}
                <span>{company.companyName}</span>
              </div>
            )
          },
          {
            key: "subscriptionStatus",
            label: "Status",
            sortValue: (company) => (company.active === 0 ? "Awaiting Approval" : company.subscriptionStatus || "Inactive"),
            render: (company) => (
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${company.subscriptionStatus === "Active" && Number(company.active) !== 0 ? "bg-green-100 text-green-700" : isSubscriptionDue(company) ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                {company.active === 0 ? "Awaiting Approval" : isSubscriptionDue(company) ? "Payment Due" : company.subscriptionStatus || "Inactive"}
              </span>
            )
          },
          {
            key: "subscriptionEnd",
            label: "Expires",
            sortValue: (company) => new Date(company.subscriptionEnd || 0).getTime(),
            render: (company) => (company.subscriptionEnd ? new Date(company.subscriptionEnd).toLocaleDateString() : "-")
          },
          { key: "email", label: "Email", render: (company) => company.email || "-" },
          { key: "phone", label: "Phone", render: (company) => company.phone || "-" },
          { key: "address", label: "Address", render: (company) => company.address || "-" }
        ]}
        rows={filteredCompanies}
        loading={loading}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search by company name, email, phone, address"
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        extraActions={(company) => (
          <>
            <button
              type="button"
              onClick={() => openInvoice(company)}
              className="px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium"
            >
              Invoice
            </button>
            {company.subscriptionStatus !== "Active" || Number(company.active) === 0 ? (
              <button
                type="button"
                onClick={() => handleApprove(company)}
                className="px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-medium"
              >
                Approve
              </button>
            ) : null}
          </>
        )}
        emptyMessage="No companies found"
      />

      <Modal
        open={modalOpen}
        title={editingCompany ? "Edit Company" : "Create Company"}
        onClose={closeModal}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Company Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="companyName"
              value={form.companyName}
              onChange={onChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              disabled={saving}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Company Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoFileChange}
              className="w-full text-sm text-gray-600"
              disabled={saving}
            />
            {form.logo ? (
              <img
                src={resolveAssetUrl(form.logo)}
                alt="Company logo preview"
                className="mt-3 h-14 w-14 rounded-lg border border-gray-200 object-contain bg-white p-1"
              />
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              disabled={saving}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
              <div className="grid grid-cols-3 gap-3">
                <select
                  name="countryCode"
                  value={form.countryCode}
                  onChange={onChange}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={saving}
                >
                  {COUNTRY_CODE_OPTIONS.map((item) => (
                    <option key={item.code} value={item.code}>{item.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="1234567"
                  className="col-span-2 w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={saving}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={onChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Contact Person</label>
              <input
                type="text"
                name="contactPerson"
                value={form.contactPerson}
                onChange={onChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Subscription Status</label>
              <select
                name="subscriptionStatus"
                value={form.subscriptionStatus}
                onChange={onChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={saving}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Active Flag</label>
              <select
                name="active"
                value={form.active}
                onChange={onChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={saving}
              >
                <option value={1}>Enabled</option>
                <option value={0}>Disabled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Subscription Start</label>
              <input
                type="date"
                name="subscriptionStart"
                value={form.subscriptionStart}
                onChange={onChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Subscription End</label>
              <input
                type="date"
                name="subscriptionEnd"
                value={form.subscriptionEnd}
                onChange={onChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={saving}
              />
            </div>
          </div>

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold disabled:opacity-60"
              disabled={saving}
            >
              {saving ? "Saving..." : editingCompany ? "Update Company" : "Create Company"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        message={deleteTarget ? `Delete company "${deleteTarget.companyName}"? This action cannot be undone.` : ""}
        loading={Boolean(deletingId)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      {invoiceTarget ? (
        <Modal
          open={Boolean(invoiceTarget)}
          title={`Send Invoice — ${invoiceTarget.companyName}`}
          onClose={closeInvoice}
          size="lg"
          footer={
            <>
              <button
                type="button"
                onClick={() => closeInvoice()}
                disabled={sendingInvoice}
                className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendInvoice}
                disabled={sendingInvoice}
                className="px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold disabled:opacity-60"
              >
                {sendingInvoice ? "Sending..." : `Send Invoice to ${invoiceTarget.email || "company"}`}
              </button>
            </>
          }
        >
          <p className="text-sm text-gray-600 mb-4">
            This emails a subscription invoice for the amount due. After payment is confirmed, approve the account to send a payment receipt.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Subscription Plan</label>
              <select
                name="subscriptionPlan"
                value={invoiceForm.subscriptionPlan}
                onChange={onInvoiceFieldChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select plan</option>
                {subscriptionPlans.map((plan) => (
                  <option key={plan.id} value={plan.name}>{plan.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Billing Cycle</label>
              <select
                name="subscriptionCycle"
                value={invoiceForm.subscriptionCycle}
                onChange={onInvoiceFieldChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="Monthly">Monthly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Amount Due</label>
              <input
                type="number"
                name="subscriptionAmount"
                min="0"
                step="0.01"
                value={invoiceForm.subscriptionAmount}
                onChange={onInvoiceFieldChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Contact Channel</label>
              <select
                name="paymentContactMethod"
                value={invoiceForm.paymentContactMethod}
                onChange={onInvoiceFieldChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="Email">Email</option>
                <option value="Phone">Phone</option>
                <option value="WhatsApp">WhatsApp</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Period Start</label>
              <input
                type="date"
                name="subscriptionStart"
                value={invoiceForm.subscriptionStart}
                onChange={onInvoiceFieldChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Period End / Due Date</label>
              <input
                type="date"
                name="subscriptionEnd"
                value={invoiceForm.subscriptionEnd}
                onChange={onInvoiceFieldChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice Notes</label>
              <textarea
                rows="3"
                name="subscriptionNotes"
                value={invoiceForm.subscriptionNotes}
                onChange={onInvoiceFieldChange}
                placeholder="Renewal invoice for the next billing period..."
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export default SuperAdminCompanies;
